/**
 * Forms Component - AUSTRAC Compliance Forms Library
 * Features: Categorized forms, Search, Download/Preview, Status tracking
 */

import React, { useMemo, useState } from "react";
import "../Forms.css";
import PortalHeader from "../PortalHeader";
import {
  FORM_CATEGORIES,
  FormItem,
  getFileTypeColor,
  getFileTypeIcon,
} from "./formsData";

const Forms: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>("cdd");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMandatory, setFilterMandatory] = useState<boolean | null>(null);

  const categories = FORM_CATEGORIES;

  const currentCategory = categories.find((c) => c.id === activeCategory);

  // Filter forms based on search and mandatory filter
  const filteredForms = useMemo(() => {
    if (!currentCategory) return [];
    return currentCategory.forms.filter((form) => {
      const matchesSearch =
        form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.tags?.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase()),
        );

      const matchesMandatory =
        filterMandatory === null || form.mandatory === filterMandatory;

      return matchesSearch && matchesMandatory;
    });
  }, [currentCategory, searchTerm, filterMandatory]);

  const handleDownload = (form: FormItem) => {
    // Open the actual AUSTRAC URL in a new tab
    if (form.downloadUrl) {
      window.open(form.downloadUrl, "_blank", "noopener,noreferrer");
    } else {
      // Fallback for forms without URLs
      alert(`This form does not have a direct link available.`);
    }
  };

  const totalForms = categories.reduce((sum, cat) => sum + cat.forms.length, 0);
  const mandatoryForms = categories.reduce(
    (sum, cat) => sum + cat.forms.filter((f) => f.mandatory).length,
    0,
  );

  return (
    <div className="forms-container">
      <PortalHeader />
      <div className="forms-wrapper">
        <div className="forms-header">
          <div className="relative pl-5">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-indigo-500 to-violet-600 rounded-full" />
            <h1>AUSTRAC Compliance Resources</h1>
            <p className="forms-subtitle">
              Official Forms, Guidance & Regulatory Information
            </p>
            <p className="forms-count">
              {totalForms} resources available • {mandatoryForms} mandatory •
              Links to austrac.gov.au
            </p>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="forms-toolbar">
          <div className="forms-search-bar">
            <input
              type="text"
              placeholder="Search forms by name, description, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="forms-search-input"
              data-testid="forms-search-input"
            />
            <span className="forms-search-icon">🔍</span>
          </div>
          <div className="forms-filter-group">
            <button
              className={`forms-filter-btn ${filterMandatory === null ? "active" : ""}`}
              onClick={() => setFilterMandatory(null)}
            >
              All Forms
            </button>
            <button
              className={`forms-filter-btn ${filterMandatory === true ? "active" : ""}`}
              onClick={() => setFilterMandatory(true)}
            >
              Mandatory
            </button>
            <button
              className={`forms-filter-btn ${filterMandatory === false ? "active" : ""}`}
              onClick={() => setFilterMandatory(false)}
            >
              Optional
            </button>
          </div>
        </div>

        <div className="forms-main-container">
          {/* Category Tabs */}
          <div className="forms-tabs">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`forms-tab ${activeCategory === category.id ? "active" : ""}`}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSearchTerm("");
                }}
              >
                <span className="forms-tab-icon">{category.icon}</span>
                <span className="forms-tab-title">{category.title}</span>
                <span className="forms-tab-count">{category.forms.length}</span>
              </button>
            ))}
          </div>

          {/* Forms Content */}
          <div className="forms-content">
            {currentCategory && (
              <>
                <div className="forms-section-header">
                  <div className="forms-section-title-bar">
                    <h2 className="forms-section-title">
                      {currentCategory.icon} {currentCategory.title}
                    </h2>
                    <span className="forms-section-count">
                      {filteredForms.length} form
                      {filteredForms.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {currentCategory.description && (
                    <p className="forms-section-description">
                      {currentCategory.description}
                    </p>
                  )}
                </div>

                <div className="forms-list">
                  {filteredForms.length > 0 ? (
                    filteredForms.map((form, index) => (
                      <div
                        key={form.id}
                        className="form-card"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="form-card-header">
                          <div className="form-card-icon-wrapper">
                            <span
                              className="form-card-icon"
                              style={{ color: getFileTypeColor(form.fileType) }}
                            >
                              {getFileTypeIcon(form.fileType)}
                            </span>
                            <span
                              className="form-file-type"
                              style={{
                                backgroundColor: getFileTypeColor(
                                  form.fileType,
                                ),
                              }}
                            >
                              {form.fileType}
                            </span>
                          </div>
                          <div className="form-card-badges">
                            {form.mandatory && (
                              <span className="form-badge mandatory">
                                Required
                              </span>
                            )}
                            <span className="form-version">
                              v{form.version}
                            </span>
                          </div>
                        </div>

                        <div className="form-card-body">
                          <h3 className="form-name">{form.name}</h3>
                          <p className="form-description">{form.description}</p>

                          {form.tags && form.tags.length > 0 && (
                            <div className="form-tags">
                              {form.tags.map((tag) => (
                                <span key={tag} className="form-tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="form-card-footer">
                          <div className="form-meta">
                            <span className="form-date">
                              Updated:{" "}
                              {new Date(form.lastUpdated).toLocaleDateString()}
                            </span>
                            {form.fileSize && (
                              <span className="form-size">{form.fileSize}</span>
                            )}
                          </div>
                          <div className="form-actions">
                            <button
                              className="form-action-btn download"
                              onClick={() => handleDownload(form)}
                              title="Open on AUSTRAC website"
                            >
                              🔗 Open on AUSTRAC
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="forms-no-results">
                      <p>No forms match your search criteria.</p>
                      <button
                        className="forms-clear-search"
                        onClick={() => {
                          setSearchTerm("");
                          setFilterMandatory(null);
                        }}
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="forms-footer">
          <p>
            All links direct to official AUSTRAC resources at{" "}
            <a
              href="https://www.austrac.gov.au"
              target="_blank"
              rel="noopener noreferrer"
            >
              austrac.gov.au
            </a>
          </p>
          <p className="forms-disclaimer">
            Links verified as of February 2026. For the latest information,
            visit the official AUSTRAC website.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Forms;
